<script setup lang="ts">
import { onMounted, ref, watch, type Component } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import UiChangesGoals from "./UiChangesGoals.vue";
import UiChangesScreenStep from "./UiChangesScreenStep.vue";
import UiChangesTabsStep from "./UiChangesTabsStep.vue";
import UiChangesMenuStep from "./UiChangesMenuStep.vue";
import {
  autoOpenUiChanges,
  closeUiChanges,
  isUiChangesOpen,
} from "@/stores/UiChangesState";
import { openDisplayDialog } from "@/stores/DisplayDialogState";

/**
 * Modale « L'interface évolue » : la nouvelle interface en quatre étapes.
 * S'ouvre d'elle-même à la première entrée dans l'outil sur cet appareil
 * (cf. UiChangesState), puis depuis le haut du volet Paramètres.
 */
const STEPS: { key: string; component: Component }[] = [
  { key: "why", component: UiChangesGoals },
  { key: "screen", component: UiChangesScreenStep },
  { key: "tabs", component: UiChangesTabsStep },
  { key: "menu", component: UiChangesMenuStep },
];

const step = ref(0);

// Chaque ouverture repart du début.
watch(isUiChangesOpen, (open) => {
  if (open) step.value = 0;
});

// Le plan vient de s'afficher (l'outil n'est monté qu'après le chargement) :
// un court délai laisse l'écran se poser avant la présentation.
onMounted(() => {
  window.setTimeout(() => autoOpenUiChanges(), 400);
});

function onModelUpdate(open: boolean) {
  if (!open) closeUiChanges();
}

function next() {
  if (step.value < STEPS.length - 1) step.value++;
  else closeUiChanges();
}

function openDisplay() {
  closeUiChanges();
  openDisplayDialog("ui_changes");
}
</script>

<template>
  <el-dialog
    :model-value="isUiChangesOpen"
    class="ui-changes-dialog"
    width="min(780px, calc(100vw - 16px))"
    :show-close="false"
    append-to-body
    align-center
    @update:model-value="onModelUpdate"
  >
    <template #header="{ titleId }">
      <div class="ui-changes-header">
        <span class="ui-changes-badge">
          <PhosphorIcon name="sparkle" :size="18" weight="fill" />
        </span>
        <span :id="titleId" class="ui-changes-title">
          {{ $t("canvas:uiChanges.title") }}
        </span>
        <DRBtnIcon
          type="button"
          variant="flat-white"
          :size="30"
          phosphor-icon="x"
          :aria-label="$t('close')"
          @click="closeUiChanges"
        />
      </div>
      <nav class="ui-changes-steps">
        <button
          v-for="(item, index) in STEPS"
          :key="item.key"
          type="button"
          class="ui-changes-step"
          :class="{ active: step === index, done: index < step }"
          :aria-current="step === index ? 'step' : undefined"
          @click="step = index"
        >
          <span class="ui-changes-step-number">{{ index + 1 }}</span>
          <span class="ui-changes-step-label">
            {{ $t(`canvas:uiChanges.steps.${item.key}`) }}
          </span>
        </button>
      </nav>
    </template>

    <component
      :is="STEPS[step].component"
      :key="STEPS[step].key"
      class="ui-changes-step-body"
      @open-display="openDisplay"
    />

    <template #footer>
      <div class="d-flex align-items-center justify-content-between gap-2">
        <DRBtn
          v-if="step > 0"
          variant="outline-white"
          size="sm"
          phosphor-icon="arrow-left"
          :label="$t('canvas:uiChanges.previous')"
          @click="step--"
        />
        <span v-else></span>
        <DRBtn
          variant="primary"
          size="sm"
          :label="
            step < STEPS.length - 1
              ? $t('canvas:uiChanges.next')
              : $t('canvas:uiChanges.done')
          "
          @click="next"
        />
      </div>
    </template>
  </el-dialog>
</template>

<!-- Non scopé : el-dialog est téléporté dans <body> -->
<style lang="scss">
.el-dialog.ui-changes-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 16px);
  margin: 0 auto;
  padding: 16px 20px;
  border: 1px solid var(--canvas-border);
  border-radius: 16px;
  background-color: var(--canvas-bg);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  color: #fff;

  .el-dialog__header {
    padding: 0;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    padding: 16px 0;
    overflow-y: auto;
  }

  .el-dialog__footer {
    padding: 12px 0 0;
    border-top: 1px solid var(--canvas-border);
  }

  .ui-changes-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ui-changes-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background-color: var(--canvas-btn-active-bg);
    color: #1a1a1a;
  }

  .ui-changes-title {
    flex-grow: 1;
    font-size: 18px;
    font-weight: 700;
  }

  .ui-changes-steps {
    display: flex;
    gap: 6px;
    margin-top: 14px;
  }

  .ui-changes-step {
    display: flex;
    flex: 1 1 0;
    align-items: center;
    gap: 6px;
    min-width: 0;
    padding: 6px 4px 8px;
    border: none;
    border-bottom: 3px solid rgba(255, 255, 255, 0.12);
    background: transparent;
    color: rgba(255, 255, 255, 0.55);
    font-size: 13px;
    cursor: pointer;

    &.done {
      border-bottom-color: var(--canvas-btn-mode-border);
      color: rgba(255, 255, 255, 0.75);
    }

    &.active {
      border-bottom-color: var(--canvas-btn-active-bg);
      color: #fff;
      font-weight: 600;
    }
  }

  .ui-changes-step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.12);
    font-size: 11px;
    font-weight: 700;

    .ui-changes-step.active & {
      background-color: var(--canvas-btn-active-bg);
      color: #1a1a1a;
    }
  }

  .ui-changes-step-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Écran étroit : les numéros suffisent, le titre de l'étape en cours reste.
  @media (max-width: 520px) {
    .ui-changes-step:not(.active) .ui-changes-step-label {
      display: none;
    }

    .ui-changes-step:not(.active) {
      flex: 0 0 auto;
    }
  }
}
</style>
